import React, { useEffect, useRef, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ShareIcon from '@atlaskit/icon/core/share';
import type { FlagsStateContainer } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController';
import { useShareAndRestrictButtonState } from '@confluence/share-and-restrict-dialog/entry-points/useShareAndRestrictButtonState';
import { useCopyContentLinkState } from '@confluence/copy-content-link/entry-points/useCopyContentLinkState';
import { expValEquals } from '@confluence/feature-experiments';

// Arguments expected by the custom hook.
export type UseShareNewDatabaseFlagProps = {
	isNewDatabase: boolean;
	isMobile: boolean;
	flags: FlagsStateContainer;
	dialogs: DialogsStateContainer;
	contentId: string;
	spaceKey: string;
	hasError?: boolean;
};

// SHARE_NEW_DATABASE_FLAG_NAME is the prompt
export const SHARE_NEW_DATABASE_FLAG_NAME = 'shareNewDatabaseFlag';
// FIVE_MINUTES_TIMEOUT stores the timeout duration (5 minutes = 300000 ms)
const FIVE_MINUTES_TIMEOUT = 300000;

// Defines what the prompt/flag will say.
const i18n = defineMessages({
	shareFlagTitle: {
		id: 'experiment-share-new-database.flag-title',
		description: 'Title displayed on share flag.',
		defaultMessage: 'Share this database with your team',
	},
	shareFlagDescription: {
		id: 'experiment-share-new-database.flag-description',
		description: 'Description displayed on share flag.',
		defaultMessage: 'Collaborate with others in real-time with live editing, comments, and more.',
	},
	shareFlagCTA: {
		id: 'experiment-share-new-database.flag-cta',
		description: 'The button text to share database.',
		defaultMessage: 'Share database',
	},
});

export function useShareNewDatabaseFlag({
	isNewDatabase,
	isMobile = false,
	flags,
	dialogs,
	spaceKey,
	contentId,
	hasError,
}: UseShareNewDatabaseFlagProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const shareDatabaseFlagTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { hasClickedShareButton } = useShareAndRestrictButtonState();
	const { hasClickedCopyContentLink } = useCopyContentLinkState();

	const handleDismissFlag = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissFlag',
				source: SHARE_NEW_DATABASE_FLAG_NAME,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleShowShareAndRestrictDialog = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_NEW_DATABASE_FLAG_NAME,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ShareDatabase',
			},
		}).fire();
		void flags.hideFlag(SHARE_NEW_DATABASE_FLAG_NAME);
		dialogs.showModal(ShareAndRestrictDialogController, {
			contentId,
			contentType: 'database',
			allowRestrictions: true,
			pageModes: 'view',
			refetchButtonData: () => {},
			isUnpublishedDraft: false,
			spaceKey,
			openFrom: SHARE_NEW_DATABASE_FLAG_NAME,
		});
	}, [createAnalyticsEvent, contentId, spaceKey, dialogs, flags]);

	useEffect(() => {
		if (fg('cc_share_new_database_gate')) {
			// Only set the timer if we are NOT on Mobile and it IS a new database.
			if (isNewDatabase && !isMobile && !hasError) {
				shareDatabaseFlagTimeoutRef.current = setTimeout(() => {
					if (!hasClickedShareButton && !hasClickedCopyContentLink) {
						if (expValEquals('cc_share_new_database', 'cohort', 'experiment')) {
							// Show the flag once the 5-minute timer runs out.
							void flags.showFlag({
								id: SHARE_NEW_DATABASE_FLAG_NAME,
								onClose: handleDismissFlag,
								close: 'never',
								type: 'custom',
								customIcon: <ShareIcon label="" />,
								title: formatMessage(i18n.shareFlagTitle),
								description: formatMessage(i18n.shareFlagDescription),
								actions: [
									{
										content: formatMessage(i18n.shareFlagCTA),
										onClick: () => handleShowShareAndRestrictDialog(),
									},
								],
							});
							createAnalyticsEvent({
								type: 'sendScreenEvent',
								data: {
									name: SHARE_NEW_DATABASE_FLAG_NAME,
								},
							}).fire();
						}
					}
				}, FIVE_MINUTES_TIMEOUT);
			}

			return () => {
				// Cleanup --> stop timer & hide prompt
				if (shareDatabaseFlagTimeoutRef.current) {
					clearTimeout(shareDatabaseFlagTimeoutRef.current);
				}
				void flags.hideFlag(SHARE_NEW_DATABASE_FLAG_NAME);
			};
		}
	}, [
		isNewDatabase,
		isMobile,
		createAnalyticsEvent,
		flags,
		formatMessage,
		handleDismissFlag,
		hasClickedShareButton,
		hasClickedCopyContentLink,
		handleShowShareAndRestrictDialog,
		hasError,
	]);
}
